import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Plus,
  Trash2,
  Upload,
  Send,
  ChevronDown,
  Save,
  ArrowLeft,
  Loader2,
  AlertCircle,
  CheckCircle,
  X,
  FileText,
  ChevronRight,
} from 'lucide-react';
import axios from '../axiosInstance';

/* ───────────── helpers ───────────── */
const getRoleName = () => {
  try {
    const user = JSON.parse(localStorage.getItem('user'));
    return user?.roles?.[0]?.name || '';
  } catch {
    return '';
  }
};

/** Normalize role: "Branch Worker" | "Branch-Worker" | "branch_worker" → "branchworker" */
const norm = (r) => r.toLowerCase().replace(/[\s_-]/g, '');
const isAdmin = (role) => norm(role) === 'admin';
const isBranchWorker = (role) => norm(role) === 'branchworker';
const canAccessForm = (role) => isAdmin(role) || isBranchWorker(role);

/* ───────────── component ───────────── */
export default function StoreIncidentalForm() {
  const navigate = useNavigate();
  const { id } = useParams(); // undefined for create, present for edit
  const isEdit = Boolean(id);

  const [role] = useState(getRoleName());

  /* master data */
  const [seasons, setSeasons] = useState([]);
  const [commodities, setCommodities] = useState([]);
  const [states, setStates] = useState([]);
  const [branches, setBranches] = useState([]);
  const [particulars, setParticulars] = useState([]);

  /* form header */
  const [header, setHeader] = useState({
    season_id: '',
    commodity_id: '',
    state_id: '',
    branch_id: '',
    procurement_quantity: '',
  });

  /* item rows */
  const [rows, setRows] = useState([
    {
      _key: Date.now(),
      id: null,
      particular_id: '',
      incidental_amount_claimed: '',
      remark: '',
      incidentalRate: 0,
      approvedAmount: 0,
      files: [],          // new files to upload (File[])
      existingDocs: [],   // already-uploaded URLs (string[])
    },
  ]);

  /* UI states */
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingRow, setUploadingRow] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [existingStatus, setExistingStatus] = useState('');

  /* ── access guard: only admin + branch-worker can use this form ── */
  useEffect(() => {
    if (!canAccessForm(role)) {
      navigate('/incidental/store', { replace: true });
    }
  }, [role, navigate]);

  /* ── load master data ── */
  useEffect(() => {
    const load = async () => {
      try {
        const promises = [
          axios.get('seasons?paginate=false'),
          axios.get('commodity'),
          axios.get('states'),
          axios.get('particulars?paginate=false'),
        ];
        if (isAdmin(role)) {
          promises.push(axios.get('user/branches?paginate=false'));
        }
        const results = await Promise.all(promises);
        setSeasons(
          results[0].data?.seasons ||
            results[0].data?.data ||
            (Array.isArray(results[0].data) ? results[0].data : []),
        );
        setCommodities(
          results[1].data?.commodities ||
            results[1].data?.data ||
            (Array.isArray(results[1].data) ? results[1].data : []),
        );
        setStates(
          results[2].data?.states ||
            results[2].data?.data ||
            (Array.isArray(results[2].data) ? results[2].data : []),
        );
        setParticulars(
          results[3].data?.particulars ||
            results[3].data?.data ||
            (Array.isArray(results[3].data) ? results[3].data : []),
        );
        if (results[4]) {
          setBranches(
            results[4].data?.data ||
              results[4].data?.branches ||
              (Array.isArray(results[4].data) ? results[4].data : []),
          );
        }
      } catch (err) {
        console.error('Master data load error:', err);
        setError('Failed to load form data. Please try again.');
      }
    };
    load();
  }, [role]);

  /* ── load existing record for edit ── */
  useEffect(() => {
    if (!isEdit) return;
    const loadRecord = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`store-incidentals/${id}`);
        const d = res.data?.data || res.data;
        const recordStatus = d.status || '';

        // Guard: branch-workers can only edit draft / returned records
        if (!isAdmin(role) && !['draft', 'returned'].includes(recordStatus)) {
          navigate(`/incidental/store/${id}`, { replace: true });
          return;
        }

        setExistingStatus(recordStatus);
        setHeader({
          season_id: d.season?.id || d.season_id || '',
          commodity_id: d.commodity?.id || d.commodity_id || '',
          state_id: d.state?.id || d.state_id || '',
          branch_id: d.branch?.id || d.branch_id || '',
          procurement_quantity: d.procurement_quantity || '',
        });
        if (d.items && d.items.length > 0) {
          setRows(
            d.items.map((item, idx) => ({
              _key: Date.now() + idx,
              id: item.id,
              particular_id: item.particular?.id || item.particular_id || '',
              incidental_amount_claimed: item.incidental_amount_claimed || '',
              remark: item.remark || '',
              incidentalRate: parseFloat(item.incidental_rate) || 0,
              approvedAmount: parseFloat(item.approval_amount) || 0,
              files: [],
              existingDocs: Array.isArray(item.supporting_document)
                ? item.supporting_document
                : item.supporting_document
                ? [item.supporting_document]
                : [],
            })),
          );
        }
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load record');
      } finally {
        setLoading(false);
      }
    };
    loadRecord();
  }, [id, isEdit, role, navigate]);

  /* ── rate preview ── */
  const fetchRatePreview = useCallback(
    async (rowKey, particular_id) => {
      if (
        !header.season_id ||
        !header.commodity_id ||
        !header.procurement_quantity ||
        !particular_id
      )
        return;
      try {
        const res = await axios.get('store-incidentals/rate-preview', {
          params: {
            season_id: header.season_id,
            commodity_id: header.commodity_id,
            particular_id,
            procurement_quantity: header.procurement_quantity,
          },
        });
        const data = res.data;
        setRows((prev) =>
          prev.map((r) =>
            r._key === rowKey
              ? {
                  ...r,
                  incidentalRate: parseFloat(data.incidental_rate) || 0,
                  approvedAmount: parseFloat(data.approval_amount) || 0,
                }
              : r,
          ),
        );
      } catch (err) {
        console.error('Rate preview error:', err);
      }
    },
    [header.season_id, header.commodity_id, header.procurement_quantity],
  );

  /* ── handlers ── */
  const handleHeaderChange = (e) => {
    const { name, value } = e.target;
    setHeader((prev) => ({ ...prev, [name]: value }));
  };

  const addRow = () => {
    setRows((prev) => [
      ...prev,
      {
        _key: Date.now(),
        id: null,
        particular_id: '',
        incidental_amount_claimed: '',
        remark: '',
        incidentalRate: 0,
        approvedAmount: 0,
        files: [],
        existingDocs: [],
      },
    ]);
  };

  const removeRow = (key) => {
    if (rows.length <= 1) return;
    setRows((prev) => prev.filter((r) => r._key !== key));
  };

  const handleRowChange = (key, field, value) => {
    setRows((prev) =>
      prev.map((r) => (r._key === key ? { ...r, [field]: value } : r)),
    );
    if (field === 'particular_id') {
      fetchRatePreview(key, value);
    }
  };

  const handleFileSelect = (key, newFiles) => {
    // newFiles is a FileList from the input
    setRows((prev) =>
      prev.map((r) =>
        r._key === key
          ? { ...r, files: [...r.files, ...Array.from(newFiles)] }
          : r,
      ),
    );
  };

  const handleRemoveNewFile = (key, fileIndex) => {
    setRows((prev) =>
      prev.map((r) =>
        r._key === key
          ? { ...r, files: r.files.filter((_, i) => i !== fileIndex) }
          : r,
      ),
    );
  };

  /* ── validation ── */
  const validate = (forSubmit = false) => {
    if (
      !header.season_id ||
      !header.commodity_id ||
      !header.state_id ||
      !header.procurement_quantity
    ) {
      setError(
        'Please fill all header fields: Season, Commodity, State, and Procurement Quantity.',
      );
      return false;
    }
    if (isAdmin(role) && !header.branch_id) {
      setError('Admin must select a Branch.');
      return false;
    }
    if (parseFloat(header.procurement_quantity) <= 0) {
      setError('Procurement Quantity must be greater than zero.');
      return false;
    }

    // check rows
    const validRows = rows.filter((r) => r.particular_id);
    if (forSubmit && validRows.length === 0) {
      setError(
        'At least one item row with a Particular is required before submitting.',
      );
      return false;
    }

    // duplicate check
    const particularIds = validRows.map((r) => r.particular_id);
    if (new Set(particularIds).size !== particularIds.length) {
      setError(
        'Duplicate Particulars are not allowed. Please select unique rows.',
      );
      return false;
    }

    setError('');
    return true;
  };

  /* ── upload files for a single row (multiple files, field name = 'files') ── */
  const uploadFilesForRow = async (storeIncidentalId, itemId, files) => {
    if (!files || files.length === 0) return;
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    await axios.post(
      `store-incidentals/${storeIncidentalId}/items/${itemId}/upload`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      },
    );
  };

  /* ── save draft (create / update) ── */
  const handleSave = async () => {
    if (!validate(false)) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const body = {
        season_id: header.season_id,
        commodity_id: header.commodity_id,
        state_id: header.state_id,
        procurement_quantity: parseFloat(header.procurement_quantity),
        items: rows
          .filter((r) => r.particular_id)
          .map((r) => {
            const item = {
              particular_id: r.particular_id,
              incidental_amount_claimed:
                parseFloat(r.incidental_amount_claimed) || 0,
              remark: r.remark || '',
            };
            if (r.id) item.id = r.id;
            return item;
          }),
      };
      if (isAdmin(role) && header.branch_id) {
        body.branch_id = header.branch_id;
      }

      let savedId = id;
      if (isEdit) {
        await axios.patch(`store-incidentals/${id}`, body);
      } else {
        const res = await axios.post('store-incidentals', body);
        savedId = res.data?.data?.id || res.data?.id;
      }

      // upload files for rows that have new files
      if (savedId) {
        // Re-fetch to get item IDs
        const freshRes = await axios.get(`store-incidentals/${savedId}`);
        const freshData = freshRes.data?.data || freshRes.data;
        const freshItems = freshData.items || [];

        for (const row of rows) {
          if (row.files && row.files.length > 0) {
            // Match by particular_id
            const matchedItem = freshItems.find(
              (fi) =>
                (fi.particular?.id || fi.particular_id) === row.particular_id,
            );
            if (matchedItem) {
              try {
                setUploadingRow(row._key);
                await uploadFilesForRow(savedId, matchedItem.id, row.files);
              } catch (uploadErr) {
                console.error(
                  'File upload failed for item:',
                  matchedItem.id,
                  uploadErr,
                );
              }
            }
          }
        }
        setUploadingRow(null);
      }

      setSuccess(
        isEdit ? 'Draft updated successfully!' : 'Draft created successfully!',
      );
      setTimeout(() => {
        navigate(
          savedId ? `/incidental/store/${savedId}` : '/incidental/store',
        );
      }, 1200);
    } catch (err) {
      setError(
        err.response?.data?.message || 'Failed to save. Please try again.',
      );
    } finally {
      setSaving(false);
    }
  };

  /* ── submit for approval ── */
  const handleSubmit = async () => {
    if (!validate(true)) return;

    // If not yet saved, save first
    setSubmitting(true);
    setError('');
    setSuccess('');
    try {
      let recordId = id;
      if (!isEdit) {
        // Create first
        const body = {
          season_id: header.season_id,
          commodity_id: header.commodity_id,
          state_id: header.state_id,
          procurement_quantity: parseFloat(header.procurement_quantity),
          items: rows
            .filter((r) => r.particular_id)
            .map((r) => ({
              particular_id: r.particular_id,
              incidental_amount_claimed:
                parseFloat(r.incidental_amount_claimed) || 0,
              remark: r.remark || '',
            })),
        };
        if (isAdmin(role) && header.branch_id)
          body.branch_id = header.branch_id;
        const res = await axios.post('store-incidentals', body);
        recordId = res.data?.data?.id || res.data?.id;
      } else {
        // Update first
        const body = {
          season_id: header.season_id,
          commodity_id: header.commodity_id,
          state_id: header.state_id,
          procurement_quantity: parseFloat(header.procurement_quantity),
          items: rows
            .filter((r) => r.particular_id)
            .map((r) => {
              const item = {
                particular_id: r.particular_id,
                incidental_amount_claimed:
                  parseFloat(r.incidental_amount_claimed) || 0,
                remark: r.remark || '',
              };
              if (r.id) item.id = r.id;
              return item;
            }),
        };
        if (isAdmin(role) && header.branch_id)
          body.branch_id = header.branch_id;
        await axios.patch(`store-incidentals/${recordId}`, body);
      }

      // Upload any files
      if (recordId) {
        const freshRes = await axios.get(`store-incidentals/${recordId}`);
        const freshData = freshRes.data?.data || freshRes.data;
        const freshItems = freshData.items || [];
        for (const row of rows) {
          if (row.files && row.files.length > 0) {
            const matchedItem = freshItems.find(
              (fi) =>
                (fi.particular?.id || fi.particular_id) === row.particular_id,
            );
            if (matchedItem) {
              try {
                await uploadFilesForRow(recordId, matchedItem.id, row.files);
              } catch (uploadErr) {
                console.error('File upload failed:', uploadErr);
              }
            }
          }
        }
      }

      // Submit
      await axios.post(`store-incidentals/${recordId}/submit`);
      setSuccess('Submitted for branch approval!');
      setTimeout(() => {
        navigate(`/incidental/store/${recordId}`);
      }, 1200);
    } catch (err) {
      setError(
        err.response?.data?.message || 'Failed to submit. Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  const allHeadersFilled =
    header.season_id &&
    header.commodity_id &&
    header.state_id &&
    header.procurement_quantity;
  const isBusy = saving || submitting;

  /* ── render ── */
  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="flex flex-col items-center gap-3 text-blue-600">
          <Loader2 size={40} className="animate-spin" />
          <span className="font-bold text-sm">Loading record...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] p-4 md:p-8 text-slate-800 font-sans">
      <div className="w-full max-w-[1400px] mx-auto">
        {/* ── Breadcrumb / Back ── */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex items-center gap-3 mb-6"
        >
          <button
            onClick={() => navigate('/incidental/store')}
            className="flex items-center gap-1.5 text-sm font-bold text-slate-500 hover:text-blue-600 transition-colors"
          >
            <ArrowLeft size={18} /> Back to List
          </button>
          <ChevronRight size={14} className="text-slate-300" />
          <span className="text-sm font-bold text-slate-800">
            {isEdit ? 'Edit Draft' : 'Create New'}
          </span>
        </motion.div>

        {/* ── Page Header ── */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-3xl md:text-4xl font-black text-blue-800 tracking-tight">
            {isEdit ? 'Edit Store Incidental' : 'Create Store Incidental'}
          </h1>
          <p className="text-slate-500 mt-1 text-sm max-w-xl">
            {isEdit
              ? 'Update the incidental record details. You can save as draft or submit for approval.'
              : 'Fill the header fields, then add item rows with particulars and claim details.'}
          </p>
          {existingStatus && (
            <span className="inline-block mt-2 px-3 py-1 bg-slate-100 rounded-full text-xs font-bold text-slate-600 border border-slate-200 capitalize">
              Status: {existingStatus.replace(/_/g, ' ')}
            </span>
          )}
        </motion.div>

        {/* ── Messages ── */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="mb-6 flex items-start gap-3 p-4 bg-red-50 border border-red-200 text-red-700 rounded-2xl text-sm font-semibold"
            >
              <AlertCircle size={20} className="shrink-0 mt-0.5" />{' '}
              <span className="flex-1">{error}</span>
              <button
                onClick={() => setError('')}
                className="p-1 hover:bg-red-100 rounded-lg"
              >
                <X size={16} />
              </button>
            </motion.div>
          )}
          {success && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="mb-6 flex items-center gap-3 p-4 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-2xl text-sm font-semibold"
            >
              <CheckCircle size={20} /> {success}
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── Header Fields Card ── */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl shadow-sm border border-slate-200 p-6 md:p-8 mb-8"
        >
          <h2 className="text-lg font-black text-slate-800 mb-5 flex items-center gap-2">
            <FileText size={20} className="text-blue-600" /> Header Information
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Season */}
            <div className="space-y-2">
              <label className="text-sm font-bold text-slate-700 ml-1">
                Season <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <select
                  name="season_id"
                  value={header.season_id}
                  onChange={handleHeaderChange}
                  className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-4 py-3 appearance-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all cursor-pointer text-sm"
                >
                  <option value="">Select Season</option>
                  {seasons.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  size={18}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                />
              </div>
            </div>
            {/* Commodity */}
            <div className="space-y-2">
              <label className="text-sm font-bold text-slate-700 ml-1">
                Commodity <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <select
                  name="commodity_id"
                  value={header.commodity_id}
                  onChange={handleHeaderChange}
                  className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-4 py-3 appearance-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all cursor-pointer text-sm"
                >
                  <option value="">Select Commodity</option>
                  {commodities.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.commodityname || c.name}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  size={18}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                />
              </div>
            </div>
            {/* State */}
            <div className="space-y-2">
              <label className="text-sm font-bold text-slate-700 ml-1">
                State <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <select
                  name="state_id"
                  value={header.state_id}
                  onChange={handleHeaderChange}
                  className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-4 py-3 appearance-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all cursor-pointer text-sm"
                >
                  <option value="">Select State</option>
                  {states.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  size={18}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
            {/* Procurement Quantity */}
            <div className="space-y-2">
              <label className="text-sm font-bold text-slate-700 ml-1">
                Procurement Quantity (q)<span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="procurement_quantity"
                value={header.procurement_quantity}
                onChange={handleHeaderChange}
                placeholder="Enter quantity"
                min="0"
                className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all text-sm"
              />
            </div>
            {/* Branch (admin only) */}
            {isAdmin(role) && (
              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-700 ml-1">
                  Branch <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <select
                    name="branch_id"
                    value={header.branch_id}
                    onChange={handleHeaderChange}
                    className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-4 py-3 appearance-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all cursor-pointer text-sm"
                  >
                    <option value="">Select Branch</option>
                    {branches.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.first_name || b.name}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    size={18}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                  />
                </div>
              </div>
            )}
          </div>
        </motion.div>

        {/* ── Items Table ── */}
        <AnimatePresence mode="wait">
          {allHeadersFilled ? (
            <motion.div
              key="items-table"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="bg-white rounded-3xl shadow-xl border border-slate-100 overflow-hidden mb-8"
            >
              {/* Table header */}
              <div className="p-6 md:p-8 bg-white border-b border-slate-100 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                  <h2 className="text-2xl font-black text-[#1E3A8A]">
                    Incidental Items
                  </h2>
                  <p className="text-slate-500 text-sm mt-1">
                    Add particulars with claim amounts. Rate and approval amount
                    are fetched automatically.
                  </p>
                </div>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={addRow}
                  disabled={isBusy}
                  className="bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white font-bold py-3 px-6 rounded-2xl shadow-lg shadow-emerald-100 transition-all flex items-center gap-2 text-sm"
                >
                  <Plus size={20} /> Add Row
                </motion.button>
              </div>

              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse min-w-[1050px]">
                  <thead className="bg-slate-50/50">
                    <tr>
                      <th className="px-5 py-4 text-[11px] font-bold text-slate-500 uppercase tracking-wider bg-slate-50/80">
                        Particulars
                      </th>
                      <th className="px-5 py-4 text-[11px] font-bold text-slate-500 uppercase tracking-wider bg-slate-50/80">
                        Incidental Rate (QT)
                      </th>
                      <th className="px-5 py-4 text-[11px] font-bold text-slate-500 uppercase tracking-wider bg-slate-50/80">
                        Approval Amount
                      </th>
                      <th className="px-5 py-4 text-[11px] font-bold text-slate-500 uppercase tracking-wider bg-slate-50/80">
                        Billed Amount
                      </th>
                      <th className="px-5 py-4 text-[11px] font-bold text-slate-500 uppercase tracking-wider bg-slate-50/80">
                        Remark
                      </th>
                      <th className="px-5 py-4 text-[11px] font-bold text-slate-500 uppercase tracking-wider bg-slate-50/80">
                        Document
                      </th>
                      <th className="px-5 py-4 text-[11px] font-bold text-slate-500 uppercase tracking-wider bg-slate-50/80 text-center">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    <AnimatePresence>
                      {rows.map((row) => (
                        <motion.tr
                          key={row._key}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, scale: 0.95 }}
                          transition={{ duration: 0.2 }}
                          className="hover:bg-slate-50/30 transition-colors"
                        >
                          {/* Particulars */}
                          <td className="px-4 py-5 align-top">
                            <select
                              value={row.particular_id}
                              onChange={(e) =>
                                handleRowChange(
                                  row._key,
                                  'particular_id',
                                  e.target.value,
                                )
                              }
                              disabled={isBusy}
                              className="w-full min-w-[180px] bg-white border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 outline-none transition-all shadow-sm disabled:opacity-50"
                            >
                              <option value="">Select Particular</option>
                              {particulars.map((p) => (
                                <option key={p.id} value={p.id}>
                                  {p.name}
                                </option>
                              ))}
                            </select>
                          </td>

                          {/* Rate */}
                          <td className="px-5 py-5 font-mono text-sm text-slate-500 align-top">
                            {row.incidentalRate > 0
                              ? row.incidentalRate.toFixed(2)
                              : '—'}
                          </td>

                          {/* Approval Amount */}
                          <td className="px-5 py-5 font-bold text-sm text-slate-700 align-top">
                            {row.approvedAmount > 0
                              ? `₹${row.approvedAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`
                              : '—'}
                          </td>

                          {/* Claimed Amount */}
                          <td className="px-4 py-5 align-top">
                            <input
                              type="number"
                              placeholder="Enter amount"
                              min="0"
                              value={row.incidental_amount_claimed}
                              onChange={(e) =>
                                handleRowChange(
                                  row._key,
                                  'incidental_amount_claimed',
                                  e.target.value,
                                )
                              }
                              disabled={isBusy}
                              className="w-full min-w-[140px] border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 outline-none transition-all bg-slate-50/50 disabled:opacity-50"
                            />
                          </td>

                          {/* Remark */}
                          <td className="px-4 py-5 align-top">
                            <textarea
                              placeholder="Optional remark"
                              rows="2"
                              value={row.remark}
                              onChange={(e) =>
                                handleRowChange(
                                  row._key,
                                  'remark',
                                  e.target.value,
                                )
                              }
                              disabled={isBusy}
                              className="w-full min-w-[140px] border border-slate-200 rounded-xl px-4 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none transition-all bg-slate-50/50 resize-none disabled:opacity-50"
                            />
                          </td>

                          {/* Document Upload */}
                          <td className="px-4 py-5 align-top">
                            <div className="flex flex-col gap-2 min-w-[160px]">
                              {/* Upload button */}
                              <label className="flex items-center justify-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-700 py-2.5 px-4 rounded-xl cursor-pointer transition-all border border-slate-200 border-dashed group">
                                {uploadingRow === row._key ? (
                                  <Loader2
                                    size={16}
                                    className="animate-spin text-blue-500"
                                  />
                                ) : (
                                  <Upload
                                    size={16}
                                    className="text-slate-400 group-hover:text-blue-500 transition-colors"
                                  />
                                )}
                                <span className="text-xs font-bold">
                                  {row.files.length > 0
                                    ? `+${row.files.length} new`
                                    : 'Add Files'}
                                </span>
                                <input
                                  type="file"
                                  className="hidden"
                                  accept=".pdf,.doc,.docx,.jpeg,.jpg,.png"
                                  multiple
                                  onChange={(e) =>
                                    handleFileSelect(row._key, e.target.files)
                                  }
                                  disabled={isBusy}
                                />
                              </label>

                              {/* New files queued */}
                              {row.files.length > 0 && (
                                <div className="flex flex-col gap-1">
                                  {row.files.map((f, fi) => (
                                    <div key={fi} className="flex items-center gap-1 bg-emerald-50 border border-emerald-200 rounded-lg px-2 py-1">
                                      <span className="text-[10px] text-emerald-700 font-bold truncate max-w-[110px]" title={f.name}>{f.name}</span>
                                      <button
                                        type="button"
                                        onClick={() => handleRemoveNewFile(row._key, fi)}
                                        disabled={isBusy}
                                        className="ml-auto text-emerald-400 hover:text-red-500 transition-colors"
                                      >
                                        <X size={12} />
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}

                              {/* Existing uploaded docs */}
                              {row.existingDocs.length > 0 && (
                                <div className="flex flex-col gap-1">
                                  {row.existingDocs.map((url, di) => (
                                    <a
                                      key={di}
                                      href={url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="flex items-center gap-1 text-[10px] text-blue-600 font-bold hover:text-blue-800 transition-colors truncate"
                                      title={url}
                                    >
                                      <FileText size={11} />
                                      Doc {di + 1}
                                    </a>
                                  ))}
                                </div>
                              )}

                              {row.files.length === 0 && row.existingDocs.length === 0 && (
                                <p className="text-[10px] text-slate-400 ml-1">No files</p>
                              )}
                            </div>
                          </td>

                          {/* Remove */}
                          <td className="px-5 py-5 text-center align-top">
                            <button
                              onClick={() => removeRow(row._key)}
                              disabled={rows.length <= 1 || isBusy}
                              className="p-2.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                            >
                              <Trash2 size={18} />
                            </button>
                          </td>
                        </motion.tr>
                      ))}
                    </AnimatePresence>
                  </tbody>
                </table>
              </div>

              {/* Action Bar */}
              <div className="p-6 md:p-8 bg-slate-50 border-t border-slate-100 flex flex-col sm:flex-row justify-between items-center gap-4">
                <div className="flex items-center gap-6">
                  <div className="text-right">
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">
                      Items
                    </p>
                    <p className="text-xl font-black text-[#1E3A8A]">
                      {rows.filter((r) => r.particular_id).length}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">
                      Total Claimed
                    </p>
                    <p className="text-xl font-black text-[#1E3A8A]">
                      ₹
                      {rows
                        .reduce(
                          (s, r) =>
                            s + (parseFloat(r.incidental_amount_claimed) || 0),
                          0,
                        )
                        .toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleSave}
                    disabled={isBusy}
                    className="bg-slate-700 hover:bg-slate-800 disabled:opacity-50 text-white font-bold py-3.5 px-8 rounded-2xl shadow-lg transition-all flex items-center gap-2 text-sm"
                  >
                    {saving ? (
                      <Loader2 size={18} className="animate-spin" />
                    ) : (
                      <Save size={18} />
                    )}
                    {saving ? 'Saving...' : 'Save Draft'}
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleSubmit}
                    disabled={isBusy}
                    className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-black py-3.5 px-8 rounded-2xl shadow-xl shadow-blue-100 transition-all flex items-center gap-2 text-sm"
                  >
                    {submitting ? (
                      <Loader2 size={18} className="animate-spin" />
                    ) : (
                      <Send size={18} />
                    )}
                    {submitting ? 'Submitting...' : 'Save & Submit'}
                  </motion.button>
                </div>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="prompt"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-blue-50/50 border-2 border-dashed border-blue-200 rounded-3xl p-12 text-center"
            >
              <div className="max-w-md mx-auto space-y-4">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6 text-blue-600">
                  <ChevronDown size={32} />
                </div>
                <h3 className="text-xl font-bold text-blue-900">
                  Complete Header Fields
                </h3>
                <p className="text-blue-700/70 leading-relaxed text-sm">
                  Please select a <strong>Season</strong>,{' '}
                  <strong>Commodity</strong>, <strong>State</strong>, and enter
                  the <strong>Procurement Quantity</strong> above to add
                  incidental items.
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
